import { useState, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
  X,
  Plus,
  Trash2,
  Edit2,
  Save,
  Image,
  Link,
  BookOpen,
  Users,
  FileText,
  Copy,
  Info,
  Award,
  Loader2,
  Lock,
  ExternalLink,
  Globe,
  ScrollText
} from 'lucide-react';
import TranslationsPanel from './TranslationsPanel';
import CertificateTemplateSelect from '@/components/certificate-builder/CertificateTemplateSelect';
import { CertificateTemplateType } from '@/types/certificate-template.types';
import { CourseMetadata, LearningObjective, Resource } from '@/types/course.types';
import { categoryService } from '@/services/category.service';
import { courseService, type CourseAssessmentConfigItem } from '@/services/course.service';
import CompletionConditionsSection, { type CompletionConditionsHandle } from './CompletionConditionsSection';
import {
  languageOptions
} from '@/constants/languageOptions';
import toast from 'react-hot-toast';
import { Select, SelectOption } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Input } from '@/components/ui/Input';
import {
  FileUploadField,
  TagsField,
} from '@/components/common/form-fields';

// A disabled fieldset stops form controls but not drop targets, and the file
// upload fields accept drag-and-drop on a plain div. Swallowing these in the
// capture phase keeps a published course from taking an upload.
const swallowDragEvent = (e: React.DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

interface MetadataModalProps {
  isOpen: boolean;
  onClose: () => void;
  metadata: CourseMetadata;
  courseDetails: any;
  /** Awaited before the completion-config flush, so it must report when it settles. */
  onSave: (metadata: any) => Promise<any>;
  onDelete?: () => Promise<void>;
  /** Requires the admin_course_delete permission. Defaults to false so the
   *  delete action stays closed unless a caller explicitly grants it. */
  canDelete?: boolean;
  /** Published courses are frozen: the metadata stays visible but read-only.
   *  Deleting is unaffected — that is governed by canDelete alone. */
  isPublished?: boolean;
  /** Tab to open on. Lets callers deep-link (e.g. "Manage translations"). */
  initialTab?: string;
}



const BLOOM_LEVELS = [
  {
    value: 'remember',
    label: 'Remember',
    description: 'Recall facts and basic concepts',
  },
  {
    value: 'understand',
    label: 'Understand',
    description: 'Explain ideas or concepts',
  },
  {
    value: 'apply',
    label: 'Apply',
    description: 'Use information in new situations',
  },
  {
    value: 'analyze',
    label: 'Analyze',
    description: 'Draw connections among ideas',
  },
  {
    value: 'evaluate',
    label: 'Evaluate',
    description: 'Justify a stand or decision',
  },
  {
    value: 'create',
    label: 'Create',
    description: 'Produce new or original work',
  },
];

export default function MetadataModal({
  isOpen,
  onClose,
  courseDetails,
  onSave,
  onDelete,
  canDelete = false,
  isPublished = false,
  initialTab = 'basic',
}: MetadataModalProps) {
  const navigate = useNavigate();
  const { id: courseId } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState(initialTab);

  // "Manage translations" deep-links straight to that tab rather than the
  // last one used.
  useEffect(() => {
    if (isOpen) setActiveTab(initialTab);
  }, [isOpen, initialTab]);

  // Only an ORIGINAL course can own translations; a translated copy is
  // managed from its master.
  const isMasterCourse = !courseDetails?.source_course_id;

  // A SCORM course stores a launch URL into the vendor package, not text of
  // ours, so there is nothing to translate.
  const isScormCourse = (courseDetails as any)?.source_type === 'scorm_imported';
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDeleteChecked, setConfirmDeleteChecked] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeUsers, setActiveUsers] = useState<any[]>([]);
  const [revokingUserId, setRevokingUserId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setActiveUsers([]);
      setConfirmDeleteChecked(false);
      setRevokingUserId(null);
    }
  }, [isOpen]);

  // React Hook Form setup
  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    control,
  } = useForm({
    defaultValues: {
      title: '',
      description: '',
      slug: '',
      difficulty: 'intermediate',
      category_id: '',
      category_name: '',
      // Annotated: an unannotated [] infers never[], so pushing into or assigning a
      // real objective/resource below fails to type-check.
      learning_objectives: [] as LearningObjective[],
      prerequisites: [],
      target_audience: [],
      tags: [],
      language: 'ENGLISH',
      language_code: '',
      resources: [] as Resource[],
      final_project: {
        id: '',
        title: '',
        description: '',
        estimated_time: { hours: 0, minutes: 0 },
        options: [],
        assessment_criteria: [],
        requirements: [],
        deliverables: [],
        grading_criteria: [],
      },
      assessment_overview: {
        components: [],
        passing_score: 70,
      },
      next_steps: {
        topics: [],
        resources: [],
        certificationCriteria: [],
      },
      estimated_duration: {
        total: { hours: 0, minutes: 0 },
        videos: { hours: 0, minutes: 0 },
        practice: { hours: 0, minutes: 0 },
        assessments: { hours: 0, minutes: 0 },
      },
      thumbnail_image_url: '',
      banner_image_url: '',
      certificate_template_id: '', // '' = default course certificate template
    },
  });

  // Watch all form values
  const formValues = watch();

  const [categories, setCategories] = useState<any[]>([]);
  const [showLearningObjectiveForm, setShowLearningObjectiveForm] =
    useState(false);
  const [editingLearningObjective, setEditingLearningObjective] =
    useState<LearningObjective | null>(null);
  const [showResourceForm, setShowResourceForm] = useState(false);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);

  // Select options
  const categoryOptions: SelectOption[] = categories.map((cat) => ({
    label: cat.category_name,
    value: cat._id,
  }));

  const difficultyOptions: SelectOption[] = [
    { label: 'Beginner', value: 'beginner' },
    { label: 'Intermediate', value: 'intermediate' },
    { label: 'Advanced', value: 'advanced' },
  ];

  const languageSelectOptions: SelectOption[] = languageOptions.map((lang) => ({
    label: lang.label,
    value: lang.value,
  }));

  useEffect(() => {
    fetchCategories();
  }, []);

  // Sync category_name when category_id changes
  useEffect(() => {
    if (formValues.category_id) {
      const selectedCategory = categories.find(
        (cat) => cat._id === formValues.category_id,
      );
      if (
        selectedCategory &&
        selectedCategory.category_name !== formValues.category_name
      ) {
        setValue('category_name', selectedCategory.category_name);
      }
    }
  }, [formValues.category_id, categories, setValue, formValues.category_name]);

  useEffect(() => {
    if (courseDetails) {
      reset({
        title: courseDetails.title || '',
        description: courseDetails.description || '',
        slug: courseDetails.slug || '',
        difficulty: courseDetails.difficulty?.toLowerCase() || 'intermediate',
        category_id: courseDetails.category_id || '',
        category_name:
          courseDetails.category_name || courseDetails.category || '',
        learning_objectives: courseDetails.learning_objectives || [],
        prerequisites: courseDetails.prerequisites || [],
        target_audience:
          courseDetails.target_audience || courseDetails.targetAudience || [],
        tags: courseDetails.tags || [],
        language: courseDetails.language || 'ENGLISH',
        language_code: courseDetails.language_code || '',
        resources: courseDetails.resources || [],
        final_project: courseDetails.final_project || {
          id: '',
          title: '',
          description: '',
          estimated_time: { hours: 0, minutes: 0 },
          options: [],
          assessment_criteria: [],
          requirements: [],
          deliverables: [],
          grading_criteria: [],
        },
        assessment_overview: courseDetails.assessment_overview || {
          components: [],
          passing_score: 70,
        },
        next_steps: courseDetails.next_steps || {
          topics: [],
          resources: [],
          certificationCriteria: [],
        },
        estimated_duration: courseDetails.estimated_duration || {
          total: { hours: 0, minutes: 0 },
          videos: { hours: 0, minutes: 0 },
          practice: { hours: 0, minutes: 0 },
          assessments: { hours: 0, minutes: 0 },
        },
        thumbnail_image_url:
          courseDetails.thumbnail_image_url || courseDetails.thumbnail || '',
        banner_image_url:
          courseDetails.banner_image_url || courseDetails.banner || '',
        certificate_template_id: courseDetails.certificate_template_id || '',
      });
    }
  }, [courseDetails, reset]);

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getCategoryDropdown();
      setCategories(response || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Failed to load categories');
    }
  };

  // Completion Config: which assessments must be passed. Loaded when the tab is
  // first opened rather than on modal mount, so the common case pays nothing.
  // NOTE: these must stay ABOVE the `if (!isOpen) return null` below — hooks
  // declared after an early return run conditionally and break the hook order.
  const [assessmentConfig, setAssessmentConfig] = useState<CourseAssessmentConfigItem[]>([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [assessmentsLoaded, setAssessmentsLoaded] = useState(false);
  // Server-side state of each tick, so Save Changes can send only what moved.
  const [savedRequirements, setSavedRequirements] = useState<Record<string, boolean>>({});

  // Custom completion conditions (T&C) hold their own draft and are flushed by
  // Save Changes through this handle — same deal as the ticks above.
  const completionConditionsRef = useRef<CompletionConditionsHandle | null>(null);
  // The tab list scrolls on its own, so keep the active tab in view whenever it
  // changes (e.g. "Keep Course" jumps back to Basic Info while scrolled down).
  const tabNavRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const nav = tabNavRef.current;
    const btn = nav?.querySelector<HTMLElement>(`[data-tab-id="${activeTab}"]`);
    if (!nav || !btn) return;
    const navRect = nav.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    if (btnRect.top < navRect.top) nav.scrollTop -= navRect.top - btnRect.top;
    else if (btnRect.bottom > navRect.bottom) nav.scrollTop += btnRect.bottom - navRect.bottom;
    if (btnRect.left < navRect.left) nav.scrollLeft -= navRect.left - btnRect.left;
    else if (btnRect.right > navRect.right) nav.scrollLeft += btnRect.right - navRect.right;
  }, [activeTab]);
  // Manual lesson completion. Defaults on, and flushed by Save Changes only when
  // the admin actually moved it. Never offered for SCORM, where the SCORM runtime
  // owns completion.
  // null until the saved value arrives, so the checkbox never renders a guess
  // that the fetch then contradicts a moment later.
  const [manualCompletionEnabled, setManualCompletionEnabled] = useState<boolean | null>(null);
  const [savingManualCompletion, setSavingManualCompletion] = useState(false);
  // The Completion Config panel opts out of the fieldset's dim (data-no-dim) so the
  // uploaded T&C document can stay readable, so it dims its own parts with this.
  const publishedDim = isPublished ? 'opacity-60' : '';

  // Current manual-completion setting, read when the tab is first opened.
  useEffect(() => {
    if (!isOpen || activeTab !== 'completion' || !courseId || isScormCourse) return;
    let cancelled = false;
    courseService
      .getCompletionConfig(courseId)
      .then((config) => {
        if (cancelled) return;
        setManualCompletionEnabled(config.manual_completion_enabled !== false);
      })
      .catch(() => {
        // Unknown is worse than usable: fall back to on, matching the server default.
        if (!cancelled) setManualCompletionEnabled(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, activeTab, courseId, isScormCourse]);

  useEffect(() => {
    if (!isOpen || activeTab !== 'completion' || assessmentsLoaded || !courseId) return;
    let cancelled = false;
    setAssessmentsLoading(true);
    courseService
      .listCourseAssessments(courseId)
      .then((items) => {
        if (!cancelled) {
          setAssessmentConfig(items);
          setSavedRequirements(
            Object.fromEntries(items.map((i) => [i.assessment_id, i.required_for_completion]))
          );
          setAssessmentsLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load assessments');
      })
      .finally(() => {
        if (!cancelled) setAssessmentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, activeTab, assessmentsLoaded, courseId]);

  // Refetch next time the tab is opened, so it never shows a stale list — and
  // drop any ticks that were changed but never saved.
  useEffect(() => {
    if (!isOpen) {
      setAssessmentsLoaded(false);
      setAssessmentConfig([]);
      setSavedRequirements({});
    }
  }, [isOpen]);

  // Group into Course / Module N / lesson headings, preserving the server order.
  const assessmentGroups = useMemo(() => {
    const groups: { key: string; label: string; items: CourseAssessmentConfigItem[] }[] = [];
    for (const item of assessmentConfig) {
      const key =
        item.assessment_level === 'COURSE'
          ? 'course'
          : item.assessment_level === 'MODULE'
          ? `module:${item.module_id}`
          : `content:${item.content_item_id}`;
      const label =
        item.assessment_level === 'COURSE'
          ? 'Course'
          : item.assessment_level === 'MODULE'
          ? `Module ${item.module_order}${item.parent_name ? ` — ${item.parent_name}` : ''}`
          : `Lesson ${item.module_order}.${item.content_item_order}${item.parent_name ? ` — ${item.parent_name}` : ''}`;

      const existing = groups.find((g) => g.key === key);
      if (existing) existing.items.push(item);
      else groups.push({ key, label, items: [item] });
    }
    return groups;
  }, [assessmentConfig]);

  const requiredCount = useMemo(
    () => assessmentConfig.filter((a) => a.required_for_completion).length,
    [assessmentConfig]
  );

  // Ticks the user changed since the last load/save — what Save Changes writes.
  const pendingRequirementChanges = useMemo(
    () =>
      assessmentConfig.filter(
        (a) => savedRequirements[a.assessment_id] !== a.required_for_completion
      ),
    [assessmentConfig, savedRequirements]
  );

  if (!isOpen) return null;

  const onSubmit = async (data: any) => {
    setIsSaving(true);
    try {
      // Sanitize data by removing _id fields from nested arrays to prevent MongoDB cast errors
      // Free-text fields are checked by the shared Input/Textarea/TagsField components (VAPT #12/#13).
      const sanitizedData = {
        ...data,
        // Drop blank rows left by "Add Audience"
        target_audience: (data.target_audience || []).map((a: string) => (a || '').trim()).filter((a: string) => a.length > 0),
        // Remove _id from learning objectives
        learning_objectives:
          data.learning_objectives?.map(({ _id, ...rest }: any) => rest) || [],
        // Remove _id from resources
        resources: data.resources?.map(({ _id, ...rest }: any) => rest) || [],
        // Remove _id from assessment components
        assessment_overview: data.assessment_overview
          ? {
              ...data.assessment_overview,
              components:
                data.assessment_overview.components?.map(
                  ({ _id, ...rest }: any) => rest,
                ) || [],
            }
          : undefined,
        // Remove _id from final project options and assessment criteria
        final_project: data.final_project
          ? {
              ...data.final_project,
              options:
                data.final_project.options?.map(
                  ({ _id, ...rest }: any) => rest,
                ) || [],
              assessment_criteria:
                data.final_project.assessment_criteria?.map(
                  ({ _id, ...rest }: any) => rest,
                ) || [],
            }
          : undefined,
      };
      // Certificate template is sent only when changed (an unchanged but since-deleted id would fail the save)
      if ((data.certificate_template_id || '') === (courseDetails?.certificate_template_id || '')) {
        delete sanitizedData.certificate_template_id;
      }

      await onSave(sanitizedData);
      // Completion Config lives on its own endpoints, so both halves of that tab —
      // the assessment ticks and the custom conditions — are written here rather
      // than on every click: one Save button for the whole modal.
      const requirements_saved = await persistRequirementChanges();
      const conditions_saved = (await completionConditionsRef.current?.save()) ?? true;
      if (requirements_saved && conditions_saved) toast.success('Metadata saved successfully!');
      // Don't close the modal - let user continue editing
    } catch (error) {
      console.error('Error saving metadata:', error);
      toast.error('Failed to save metadata. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const onError = (errors: any) => {
    // Find the first error and switch to that tab
    if (
      errors.title ||
      errors.description ||
      errors.category_id ||
      errors.difficulty ||
      errors.language
    ) {
      setActiveTab('basic');
      toast.error('Please fill in all required fields in Basic Info');
    }
  };

  const handleDurationChange = (
    category: string,
    field: 'hours' | 'minutes',
    value: string,
  ) => {
    const numValue = parseInt(value) || 0;
    setValue(`estimated_duration.${category}.${field}` as any, numValue);
  };

  // Learning Objectives handlers
  const saveLearningObjective = (objective: LearningObjective) => {
    const objectives = [...(formValues.learning_objectives || [])];
    if (editingLearningObjective) {
      const index = objectives.findIndex(
        (o: LearningObjective) =>
          o._id === editingLearningObjective._id ||
          o.id === editingLearningObjective.id,
      );
      if (index !== -1) {
        objectives[index] = {
          ...objective,
          _id: editingLearningObjective._id || Date.now().toString(),
          id: editingLearningObjective.id || `obj-${Date.now()}`,
        };
      }
    } else {
      objectives.push({
        ...objective,
        _id: Date.now().toString(),
        id: `obj-${Date.now()}`,
      });
    }
    setValue('learning_objectives', objectives as any);
    setEditingLearningObjective(null);
    setShowLearningObjectiveForm(false);
  };

  const deleteLearningObjective = (id: string) => {
    setValue(
      'learning_objectives',
      formValues.learning_objectives?.filter(
        (o: LearningObjective) => o._id !== id && o.id !== id,
      ) as any,
    );
  };

  // Array field handlers
  const addArrayItem = (field: string, value: string = '') => {
    const currentArray = (formValues as any)[field] || [];
    setValue(field as any, [...currentArray, value]);
  };

  const removeArrayItem = (field: string, index: number) => {
    const currentArray = (formValues as any)[field] || [];
    setValue(
      field as any,
      currentArray.filter((_: any, i: number) => i !== index),
    );
  };

  // Resource handlers
  const saveResource = (resource: Resource) => {
    const resources = [...(formValues.resources || [])];
    if (editingResource) {
      const index = resources.findIndex(
        (r: Resource) =>
          r._id === editingResource._id || r.id === editingResource.id,
      );
      if (index !== -1) {
        resources[index] = {
          ...resource,
          _id: editingResource._id || Date.now().toString(),
          id: editingResource.id,
        };
      }
    } else {
      resources.push({
        ...resource,
        _id: Date.now().toString(),
        id: Date.now().toString(),
      });
    }
    setValue('resources', resources as any);
    setEditingResource(null);
    setShowResourceForm(false);
  };

  const deleteResource = (id: string) => {
    setValue(
      'resources',
      formValues.resources?.filter(
        (r: Resource) => r._id !== id && r.id !== id,
      ) as any,
    );
  };

  // Local only — persisted by Save Changes via persistRequirementChanges.
  const toggleAssessmentRequired = (item: CourseAssessmentConfigItem) => {
    setAssessmentConfig((prev) =>
      prev.map((a) =>
        a.assessment_id === item.assessment_id
          ? { ...a, required_for_completion: !a.required_for_completion }
          : a
      )
    );
  };

  /**
   * Writes the changed completion ticks. There is no bulk endpoint, so this is
   * one PATCH per changed assessment. Never throws — a failure here must not be
   * reported as a metadata save failure. Returns true when everything landed.
   */
  /**
   * Writes the manual-completion switch as soon as it moves, rather than through
   * Save Changes: this setting stays editable on a published course, where that
   * button is deliberately absent. Optimistic, reverting if the write fails.
   */
  const handleManualCompletionToggle = async (next: boolean) => {
    if (!courseId || isScormCourse || savingManualCompletion) return;
    if (manualCompletionEnabled === null) return;

    const previous = manualCompletionEnabled;
    setManualCompletionEnabled(next);
    setSavingManualCompletion(true);

    try {
      await courseService.setManualCompletionEnabled(courseId, next);
      toast.success(
        next ? 'Learners can mark lessons complete' : 'Manual lesson completion turned off'
      );
    } catch (error) {
      console.error('Failed to save the manual completion setting', error);
      setManualCompletionEnabled(previous);
      toast.error('Failed to save the manual completion setting');
    } finally {
      setSavingManualCompletion(false);
    }
  };

  const persistRequirementChanges = async (): Promise<boolean> => {
    const changes = pendingRequirementChanges;
    if (changes.length === 0) return true;

    // Run sequentially — each backend PATCH reads the full
    // completion_config.assessments array, modifies one entry, and writes the
    // whole array back. When fired concurrently (Promise.allSettled) the calls
    // race: they all read the same stale array and the last write wins,
    // silently discarding the other changes.  Sequencing avoids this.
    const results: PromiseSettledResult<any>[] = [];
    for (const item of changes) {
      try {
        const value = await courseService.setAssessmentCompletionRequirement(
          item.assessment_id,
          item.required_for_completion
        );
        results.push({ status: 'fulfilled', value });
      } catch (reason) {
        results.push({ status: 'rejected', reason });
      }
    }

    const failed = changes.filter((_, i) => results[i].status === 'rejected');
    // Accept the ones that landed as the new baseline; roll the failed ticks
    // back so what is on screen keeps matching the server.
    setSavedRequirements((prev) => {
      const next = { ...prev };
      changes.forEach((item, i) => {
        if (results[i].status === 'fulfilled') next[item.assessment_id] = item.required_for_completion;
      });
      return next;
    });
    if (failed.length > 0) {
      const failedIds = new Set(failed.map((f) => f.assessment_id));
      setAssessmentConfig((prev) =>
        prev.map((a) =>
          failedIds.has(a.assessment_id)
            ? { ...a, required_for_completion: !a.required_for_completion }
            : a
        )
      );
      console.error('Failed to save completion requirements', failed.map((f) => f.assessment_id));
      toast.error(
        failed.length === 1
          ? `Failed to save the completion setting for ${failed[0].title}`
          : `Failed to save ${failed.length} completion settings`
      );
      return false;
    }
    return true;
  };

  const tabs = [
    {
      id: 'basic',
      label: 'Basic Info',
      icon: <FileText className="w-4 h-4" />,
    },
    {
      id: 'learning',
      label: 'Learning',
      icon: <BookOpen className="w-4 h-4" />,
    },
    { id: 'audience', label: 'Audience', icon: <Users className="w-4 h-4" /> },
    // { id: 'duration', label: 'Duration', icon: <Clock className="w-4 h-4" /> },
    { id: 'media', label: 'Media', icon: <Image className="w-4 h-4" /> },
    { id: 'resources', label: 'Resources', icon: <Link className="w-4 h-4" /> },
    { id: 'about', label: 'About', icon: <Info className="w-4 h-4" /> },
    { id: 'completion', label: 'Completion Config', icon: <Award className="w-4 h-4" /> },
    { id: 'certificate', label: 'Certificate', icon: <ScrollText className="w-4 h-4" /> },
    // Only on the original course, so nobody can translate a translation.
    ...(isMasterCourse && !isScormCourse
      ? [{ id: 'translations', label: 'Translations', icon: <Globe className="w-4 h-4" /> }]
      : []),
    // Hidden for future use
    // { id: 'assessment', label: 'Assessment', icon: <CheckCircle className="w-4 h-4" /> },
    // { id: 'project', label: 'Final Project', icon: <Award className="w-4 h-4" /> },
    // { id: 'next', label: 'Next Steps', icon: <ChevronRight className="w-4 h-4" /> },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl h-[95vh] sm:h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-white rounded-t-xl border-b px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900">
              Edit Course Metadata
            </h2>
            <button
              onClick={onClose}
              disabled={isSaving}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Main Content Area with Sidebar */}
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Sidebar Navigation */}
          {/* Tabs scroll on their own; Delete is pinned to the sidebar's bottom so it never shifts between tabs */}
          <div className="md:w-64 flex-shrink-0 border-b md:border-b-0 md:border-r bg-gray-50 flex md:flex-col overflow-hidden">
            <nav ref={tabNavRef} className="flex-1 min-w-0 overflow-x-auto md:overflow-x-visible md:overflow-y-auto p-2 md:p-4">
              <div className="flex md:flex-col space-x-1 md:space-x-0 md:space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    data-tab-id={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 md:w-full flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2 md:py-3 rounded-lg transition-all font-medium text-xs md:text-sm whitespace-nowrap md:whitespace-normal ${activeTab === tab.id
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    <span
                      className={
                        activeTab === tab.id ? 'text-white' : 'text-gray-500'
                      }
                    >
                      {tab.icon}
                    </span>
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>
            </nav>

            {onDelete && (
              <div className="flex-shrink-0 p-2 md:p-4">
                <button
                  type="button"
                  disabled={!canDelete}
                  title={!canDelete ? 'You do not have permission to delete courses' : undefined}
                  onClick={() => {
                    setActiveTab('delete');
                    setConfirmDeleteChecked(false);
                    setActiveUsers([]);
                  }}
                  className={`flex-shrink-0 md:w-full flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2 md:py-3 rounded-lg transition-all font-medium text-xs md:text-sm whitespace-nowrap md:whitespace-normal disabled:opacity-50 disabled:cursor-not-allowed ${activeTab === 'delete'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  <Trash2 className={`w-4 h-4 ${activeTab === 'delete' ? 'text-white' : 'text-gray-500'}`} />
                  <span className="hidden sm:inline">Delete Course</span>
                </button>
              </div>
            )}
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto bg-white">
            <div className="p-4 sm:p-6">
              {/* Delete Course Tab */}
              {activeTab === 'delete' && (
                <div className="space-y-6 max-w-2xl mx-auto py-2">
                  {/* Confirmation Checkbox & Separate Action Area */}
                  <div className="border border-red-100 rounded-xl p-5 bg-red-50/5">
                    <div className="max-w-xl mx-auto text-start">
                      <div className="flex items-start gap-5 mb-6">
                        {/* Icon */}
                        <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                          <Trash2 className="w-7 h-7 text-red-600" />
                        </div>

                        {/* Content */}
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Delete Course
                          </h3>

                          <p className="text-sm text-gray-600">
                            This action is permanent and cannot be undone.
                          </p>
                        </div>
                      </div>

                      {/* Checkbox */}
                      <div className="flex items-start gap-3 text-left">
                        <input
                          type="checkbox"
                          id="confirm-delete-checkbox"
                          checked={confirmDeleteChecked}
                          onChange={(e) => setConfirmDeleteChecked(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
                        />

                        <label
                          htmlFor="confirm-delete-checkbox"
                          className="text-xs text-gray-700 select-none cursor-pointer font-medium leading-relaxed"
                        >
                          I confirm that I want to delete this course and all associated
                          modules, lessons, quizzes, resources, and statistics. I understand
                          this action is permanent.
                        </label>
                      </div>

                      {activeUsers?.length > 0 && (
                        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-start">
                          <h4 className="text-xs font-bold text-amber-800 uppercase tracking-wider mb-2">
                            Active Enrolled Learners ({activeUsers?.length})
                          </h4>
                          <p className="text-xs text-amber-700 mb-3">
                            This course cannot be deleted because it is still assigned to one or more users. Please revoke the course from all assigned users first, then try deleting the course again.
                          </p>
                          <div className="max-h-40 overflow-y-auto space-y-2 pr-1">
                            {activeUsers.map((user) => (
                              <div key={user._id} className="flex items-center justify-between p-2 bg-white rounded border border-amber-100 text-xs shadow-sm">
                                <div>
                                  <div className="font-semibold text-gray-800">
                                    {user.given_name} {user.family_name || ''}
                                  </div>
                                  <div className="text-gray-500 text-[10px]">{user.email}</div>
                                </div>
                                <button
                                  type="button"
                                  disabled={revokingUserId !== null}
                                  onClick={() => {
                                    setRevokingUserId(user._id);
                                    navigate(`/admin/course/assignments/user/${user._id}/course/${courseId}/progress`);
                                  }}
                                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all disabled:opacity-50"
                                  title="Revoke user assignment"
                                >
                                  {revokingUserId === user?._id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <ExternalLink className="w-4 h-4" />
                                  )}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Buttons */}
                      <div className="flex justify-center gap-3 pt-6 mt-6 border-t border-gray-100">
                        <button
                          type="button"
                          onClick={() => {
                            setActiveTab("basic")
                            setActiveUsers([]);
                          }
                          }
                          className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Keep Course
                        </button>

                        <button
                          type="button"
                          disabled={!canDelete || !confirmDeleteChecked || isDeleting}
                          title={!canDelete ? 'You do not have permission to delete courses' : undefined}
                          onClick={async () => {
                            if (onDelete && canDelete) {
                              setIsDeleting(true);
                              setActiveUsers([]);
                              try {
                                const res: any = await onDelete();
                                const users = res?.meta_data?.active_users || res?.data?.active_users;
                                if (Array.isArray(users) && users.length > 0) {
                                  setActiveUsers(users);
                                } else {
                                  onClose();
                                }
                              } catch (err: any) {
                                console.error("Delete course failed:", err);
                                const msg = err?.response?.data?.description || err?.response?.data?.message || err?.message || 'Failed to delete course';
                                toast.error(msg);
                              } finally {
                                setIsDeleting(false);
                              }
                            }
                          }}
                          className="px-4 py-2 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 shadow-sm transition-colors"
                        >
                          {isDeleting ? (
                            <>
                              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              <span>Deleting...</span>
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-3.5 h-3.5" />
                              <span>Delete Course Permanently</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Everything below is the editable metadata. A published course is
                  frozen, so the fields stay visible but disabled — the delete tab
                  above is deliberately outside this fieldset.
                  Anchors are excluded from the not-allowed cursor: a fieldset only
                  disables form controls, so links (view an uploaded T&C PDF, open a
                  resource) still work here, and showing them as blocked when they
                  are not is just wrong. Reading is not editing.
                  The dim sits on each tab panel, not the fieldset: opacity composites
                  the subtree, so up here nothing inside could opt back out. Panels
                  marked data-no-dim dim their own parts instead. */}
              {/* Completion Config heading and the manual-completion switch sit
                  OUTSIDE the fieldset below, like the delete tab: this setting stays
                  editable on a published course, and a disabled fieldset would
                  otherwise disable it however the input itself is configured.
                  SCORM reports its own completion, so the switch is not offered there. */}
              {activeTab === 'completion' && (
                <div className="space-y-4 sm:space-y-6 mb-4 sm:mb-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-1">Completion Config</h3>
                    <p className="text-sm text-gray-500">
                      Set the conditions a learner must meet before this course is marked completed.
                    </p>
                  </div>

                  {!isScormCourse && (
                    <label
                      className={`flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 ${
                        manualCompletionEnabled === null ? 'cursor-default' : 'cursor-pointer'
                      }`}
                    >
                      {manualCompletionEnabled === null ? (
                        <Loader2 className="mt-0.5 w-4 h-4 animate-spin text-gray-400 flex-shrink-0" />
                      ) : (
                        <input
                          type="checkbox"
                          checked={manualCompletionEnabled}
                          onChange={(e) => handleManualCompletionToggle(e.target.checked)}
                          disabled={savingManualCompletion}
                          className="mt-0.5 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 disabled:opacity-50"
                        />
                      )}
                      <span className="flex-1">
                        <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                          Allow learners to mark lessons complete
                          {savingManualCompletion && (
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />
                          )}
                        </span>
                        <span className="block text-xs text-gray-500 mt-0.5">
                          When off, the tick is hidden and a lesson completes only once the learner
                          has spent enough time on it. Completed lessons still show a tick.
                          Saved as soon as you change it.
                        </span>
                      </span>
                    </label>
                  )}
                </div>
              )}

              <fieldset
                disabled={isPublished}
                onDragOverCapture={isPublished ? swallowDragEvent : undefined}
                onDropCapture={isPublished ? swallowDragEvent : undefined}
                className={isPublished ? 'cursor-not-allowed [&>*:not([data-no-dim])]:opacity-60 [&_*:not(a):not(a_*)]:cursor-not-allowed' : undefined}
              >
              {isPublished && activeTab !== 'delete' && (
                <div className="mb-4 flex items-start gap-2.5 px-3 py-2.5 bg-green-50 border border-green-200 rounded-lg">
                  <Lock className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-900">
                    This course is published, so its metadata is read-only.
                  </p>
                </div>
              )}
              {/* Basic Info Tab */}
              {activeTab === 'basic' && (
                <div className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <Input
                      name="title"
                      control={control}
                      label="Course Title"
                      required
                      placeholder="Enter course title"
                      rules={{
                        required: 'Course title is required',
                        minLength: {
                          value: 3,
                          message: 'Title must be at least 3 characters',
                        },
                        maxLength: {
                          value: 200,
                          message: 'Title must not exceed 200 characters',
                        },
                      }}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Course Slug
                      </label>
                      <div className="relative">
                        {/* System-generated and read-only, so it skips the free-text check. */}
                        <Input
                          name="slug"
                          control={control}
                          type="text"
                          allowSpecialChars
                          className="pr-10 bg-gray-50 text-gray-500 cursor-not-allowed"
                          placeholder="course-url-slug"
                          disabled
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (formValues.slug) {
                              navigator.clipboard.writeText(formValues.slug);
                              toast.success('Course slug copied to clipboard!');
                            }
                          }}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                          title="Copy course slug"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <Textarea
                    name="description"
                    control={control}
                    label="Description"
                    required
                    error={errors.description?.message as string}
                    rows={4}
                    placeholder="Enter course description"
                    rules={{
                      required: 'Course description is required',
                      minLength: {
                        value: 10,
                        message: 'Description must be at least 10 characters',
                      },
                      maxLength: {
                        value: 2000,
                        message: 'Description must not exceed 2000 characters',
                      },
                    }}
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {/* react-select renders a div, which a disabled fieldset does
                        not disable — these need isDisabled explicitly. */}
                    <Select
                      name="category_id"
                      control={control}
                      label="Category"
                      options={categoryOptions}
                      placeholder="Select Category"
                      isClearable
                      isDisabled={isPublished}
                    />

                    <Select
                      name="difficulty"
                      control={control}
                      label="Difficulty Level"
                      options={difficultyOptions}
                      placeholder="Select Difficulty"
                      isDisabled={isPublished}
                    />

                    <Select
                      name="language"
                      control={control}
                      label="Language"
                      options={languageSelectOptions}
                      placeholder="Select Language"
                      isDisabled
                    />
                  </div>

                  <TagsField
                    label="Tags"
                    value={formValues.tags || []}
                    onChange={(tags) => setValue('tags', tags as any)}
                    placeholder="Add a tag and press Enter"
                  />
                </div>
              )}

              {/* Learning Tab */}
              {activeTab === 'learning' && (
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        Learning Objectives
                      </h3>
                      <button
                        onClick={() => {
                          setEditingLearningObjective(null);
                          setShowLearningObjectiveForm(true);
                        }}
                        className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add Objective
                      </button>
                    </div>

                    {showLearningObjectiveForm && (
                      <LearningObjectiveForm
                        objective={editingLearningObjective}
                        onSave={saveLearningObjective}
                        onCancel={() => {
                          setEditingLearningObjective(null);
                          setShowLearningObjectiveForm(false);
                        }}
                      />
                    )}

                    <div className="space-y-2">
                      {formValues.learning_objectives.map(
                        (objective: LearningObjective, index: number) => (
                          <div
                            key={objective._id || objective.id || index}
                            className="border rounded-lg p-4 bg-white"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="text-gray-900">
                                  {objective.description}
                                </p>
                                <div className="flex items-center gap-2 mt-2">
                                  <span
                                    className={`px-2 py-1 text-xs font-medium rounded ${
                                      objective.bloom_level === 'create'
                                        ? 'bg-purple-100 text-purple-700'
                                        : objective.bloom_level === 'evaluate'
                                        ? 'bg-indigo-100 text-indigo-700'
                                        : objective.bloom_level === 'analyze'
                                        ? 'bg-blue-100 text-blue-700'
                                        : objective.bloom_level === 'apply'
                                        ? 'bg-green-100 text-green-700'
                                        : objective.bloom_level === 'understand'
                                        ? 'bg-yellow-100 text-yellow-700'
                                        : 'bg-gray-100 text-gray-700'
                                    }`}
                                  >
                                    Bloom: {objective.bloom_level}
                                  </span>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    setEditingLearningObjective(objective);
                                    setShowLearningObjectiveForm(true);
                                  }}
                                  className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() =>
                                    deleteLearningObjective(
                                      objective._id || objective.id || '',
                                    )
                                  }
                                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ),
                      )}
                      {formValues.learning_objectives.length === 0 &&
                        !showLearningObjectiveForm && (
                          <div className="text-gray-500 text-sm py-8 text-center border-2 border-dashed rounded-lg">
                            No learning objectives added yet. Click "Add
                            Objective" to start.
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              )}

              {/* Audience Tab */}
              {activeTab === 'audience' && (
                <div className="space-y-6">
                  {/* The shared TagsField adds the clean-text check to each entry. */}
                  <TagsField
                    label="Prerequisites"
                    value={formValues.prerequisites || []}
                    onChange={(items) => setValue('prerequisites', items as any)}
                    placeholder="Add a prerequisite and press Enter"
                  />
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-sm font-medium text-gray-700">
                        Target Audience
                      </label>
                      <button
                        onClick={() => addArrayItem('target_audience', '')}
                        className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add Audience
                      </button>
                    </div>
                    <div className="space-y-2">
                      {formValues.target_audience.map(
                        (_audience: string, index: number) => (
                          <div key={index} className="flex gap-2 items-start">
                            {/* Each row is the shared Input, so the clean-text check applies. */}
                            <div className="flex-1">
                              <Input
                                name={`target_audience.${index}` as any}
                                control={control}
                                type="text"
                                placeholder="Enter target audience"
                              />
                            </div>
                            <button
                              onClick={() =>
                                removeArrayItem('target_audience', index)
                              }
                              className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ),
                      )}
                      {formValues.target_audience.length === 0 && (
                        <div className="text-gray-500 text-sm py-4 text-center border-2 border-dashed rounded-lg">
                          No target audience defined yet. Click "Add Audience"
                          to start.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Duration Tab */}
              {activeTab === 'duration' && (
                <div className="space-y-4 sm:space-y-6">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900">
                    Estimated Duration
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <h4 className="font-medium text-gray-900 mb-3">
                        Total Duration
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">
                            Hours
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={
                              formValues.estimated_duration?.total?.hours || 0
                            }
                            onChange={(e) =>
                              handleDurationChange(
                                'total',
                                'hours',
                                e.target.value,
                              )
                            }
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">
                            Minutes
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="59"
                            value={
                              formValues.estimated_duration?.total?.minutes || 0
                            }
                            onChange={(e) =>
                              handleDurationChange(
                                'total',
                                'minutes',
                                e.target.value,
                              )
                            }
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* HIDDEN - Video Content - Keeping for future use */}
                    {/* <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="font-medium text-gray-900 mb-3">Video Content</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Hours</label>
                      <input
                        type="number"
                        min="0"
                        value={formValues.estimated_duration?.videos?.hours || 0}
                        onChange={(e) => handleDurationChange('videos', 'hours', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Minutes</label>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={formValues.estimated_duration?.videos?.minutes || 0}
                        onChange={(e) => handleDurationChange('videos', 'minutes', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div> */}

                    {/* HIDDEN - Practice Time - Keeping for future use */}
                    {/* <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="font-medium text-gray-900 mb-3">Practice Time</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Hours</label>
                      <input
                        type="number"
                        min="0"
                        value={formValues.estimated_duration?.practice?.hours || 0}
                        onChange={(e) => handleDurationChange('practice', 'hours', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Minutes</label>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={formValues.estimated_duration?.practice?.minutes || 0}
                        onChange={(e) => handleDurationChange('practice', 'minutes', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div> */}

                    {/* HIDDEN - Assessments Duration - Keeping for future use */}
                    {/* <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="font-medium text-gray-900 mb-3">Assessments</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Hours</label>
                      <input
                        type="number"
                        min="0"
                        value={formValues.estimated_duration?.assessments?.hours || 0}
                        onChange={(e) => handleDurationChange('assessments', 'hours', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Minutes</label>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={formValues.estimated_duration?.assessments?.minutes || 0}
                        onChange={(e) => handleDurationChange('assessments', 'minutes', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div> */}
                  </div>
                </div>
              )}

              {/* Media Tab */}
              {activeTab === 'media' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <FileUploadField
                      label="Thumbnail Image"
                      description="Course thumbnail image (recommended: 800x600px)"
                      currentFileUrl={formValues.thumbnail_image_url}
                      onFileChange={(url) =>
                        setValue('thumbnail_image_url', url || '')
                      }
                      accept="image/*"
                      fileType="image"
                      maxSizeMB={5}
                      metadata={{ category: 'course', type: 'thumbnail' }}
                    />

                    <FileUploadField
                      label="Banner Image"
                      description="Course banner image (recommended: 1920x600px)"
                      currentFileUrl={formValues.banner_image_url}
                      onFileChange={(url) =>
                        setValue('banner_image_url', url || '')
                      }
                      accept="image/*"
                      fileType="image"
                      maxSizeMB={5}
                      metadata={{ category: 'course', type: 'banner' }}
                    />
                  </div>
                </div>
              )}

              {/* Resources Tab */}
              {activeTab === 'resources' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Course Resources
                    </h3>
                    <button
                      onClick={() => {
                        setEditingResource(null);
                        setShowResourceForm(true);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Resource
                    </button>
                  </div>

                  {showResourceForm && (
                    <ResourceForm
                      resource={editingResource}
                      onSave={saveResource}
                      onCancel={() => {
                        setEditingResource(null);
                        setShowResourceForm(false);
                      }}
                    />
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    {formValues.resources.map((resource: Resource) => (
                      <div
                        key={resource._id || resource.id}
                        className="border rounded-lg p-4 bg-white shadow-sm"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">
                              {resource.title}
                            </h4>
                            <p className="text-sm text-gray-500 mt-1">
                              {resource.type}
                            </p>
                            {resource.description && (
                              <p className="text-sm text-gray-600 mt-2">
                                {resource.description}
                              </p>
                            )}
                            <a
                              href={resource.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-800 mt-2 inline-flex items-center gap-1"
                            >
                              <Link className="w-3 h-3" />
                              {resource.url}
                            </a>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setEditingResource(resource);
                                setShowResourceForm(true);
                              }}
                              className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() =>
                                deleteResource(
                                  resource._id || resource.id || '',
                                )
                              }
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {formValues.resources.length === 0 && !showResourceForm && (
                    <div className="text-center py-12 border-2 border-dashed rounded-lg">
                      <Link className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">No resources added yet</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Click "Add Resource" to get started
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Certificate Tab — same place as on Learning Paths and Certifications */}
              {activeTab === 'certificate' && (
                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-1">Certificate Template</h3>
                    <p className="text-sm text-gray-500">The certificate design learners get when they complete this course.</p>
                  </div>
                  <div className="max-w-xl">
                    <CertificateTemplateSelect
                      templateType={CertificateTemplateType.COURSE_COMPLETION}
                      value={watch('certificate_template_id') || ''}
                      onChange={(id) => setValue('certificate_template_id', id, { shouldDirty: true })}
                      disabled={isPublished}
                    />
                    <p className="text-xs text-gray-400 mt-2">Applies to certificates issued after you click Save Changes.</p>
                  </div>
                </div>
              )}

              {/* About Tab */}
              {activeTab === 'about' && (
                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-1">
                      About
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Course attribution (read-only).
                    </p>
                  </div>

                  <div className="max-w-md space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Created by
                      </label>
                      <select
                        disabled
                        value={courseDetails?.created_by_user?.user_id || ''}
                        className="w-full px-4 py-2 border rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed appearance-none"
                      >
                        {courseDetails?.created_by_user ? (
                          <option value={courseDetails.created_by_user.user_id}>
                            {courseDetails.created_by_user.full_name || 'Unknown user'}
                            {courseDetails.created_by_user.email
                              ? ` (${courseDetails.created_by_user.email})`
                              : ''}
                          </option>
                        ) : (
                          <option value="">Creator not available</option>
                        )}
                      </select>
                      {courseDetails?.created_by_user?.email && (
                        <p className="mt-2 text-xs text-gray-500">
                          Email: {courseDetails.created_by_user.email}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Created at
                      </label>
                      <input
                        type="text"
                        disabled
                        value={
                          courseDetails?.createdAt
                            ? new Date(courseDetails.createdAt).toLocaleString(undefined, {
                                year: 'numeric',
                                month: 'short',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : 'Not available'
                        }
                        className="w-full px-4 py-2 border rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Completion Config — custom conditions + which assessments must be
                  passed. Everything here is held locally and written by Save Changes
                  with the rest of the metadata.
                  Hidden rather than unmounted when another tab is active: the
                  conditions section holds an unsaved draft that must survive a tab
                  switch. Both of its loads stay lazy via `activeTab`. */}
              {/* Translations — acts on its own endpoints, so nothing here
                  depends on Save Changes. */}
              {activeTab === 'translations' && isMasterCourse && !isScormCourse && courseId && (
                <TranslationsPanel
                  courseId={courseId}
                  sourceLanguageCode={courseDetails?.language_code || 'en-US'}
                  isPublished={isPublished}
                  onOpenCourse={(languageCode) => {
                    onClose();
                    navigate(
                      `/admin/edit-course/${courseId}?language=${encodeURIComponent(languageCode)}`
                    );
                  }}
                />
              )}

              <div
                data-no-dim
                className={`space-y-4 sm:space-y-6 ${activeTab === 'completion' ? '' : 'hidden'}`}
              >
                {courseId && (
                  <CompletionConditionsSection
                    ref={completionConditionsRef}
                    courseId={courseId}
                    isPublished={isPublished}
                    active={activeTab === 'completion'}
                  />
                )}

                {activeTab === 'completion' && (
                  <div className={`space-y-4 sm:space-y-6 ${publishedDim}`}>
                  <div className="pt-2">
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">Required assessments</h4>
                    <p className="text-xs text-gray-500">
                      Choose which assessments a learner must <span className="font-medium text-gray-700">pass</span> before
                      this course is marked completed.
                    </p>
                  </div>

                  <div className="flex items-start gap-2.5 px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                    <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-blue-900 space-y-1">
                      <p>
                        Finishing every module is still required. A course with required assessments is only
                        completed once the modules are done <span className="font-medium">and</span> each required
                        assessment has been passed.
                      </p>
                      <p className="text-blue-800">
                        This does not hide anything from learners — every assessment stays visible and
                        attemptable either way. Your selection is applied when you click Save Changes.
                      </p>
                    </div>
                  </div>

                  {assessmentsLoading ? (
                    <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading assessments...
                    </div>
                  ) : assessmentConfig.length === 0 ? (
                    <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
                      <Award className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm font-medium text-gray-900">No assessments yet</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Add assessments from the course structure, then choose which ones gate completion here.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-5">
                      {pendingRequirementChanges.length > 0 && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
                          <Info className="w-3.5 h-3.5 flex-shrink-0" />
                          <span>
                            {pendingRequirementChanges.length} unsaved{' '}
                            {pendingRequirementChanges.length === 1 ? 'change' : 'changes'} — click
                            Save Changes to apply.
                          </span>
                        </div>
                      )}

                      <p className="text-sm text-gray-600">
                        {requiredCount === 0 ? (
                          <>
                            No assessments are required — this course completes on module progress alone.
                          </>
                        ) : (
                          <>
                            Learners must pass{' '}
                            <span className="font-semibold text-gray-900">
                              {requiredCount} of {assessmentConfig.length}
                            </span>{' '}
                            {assessmentConfig.length === 1 ? 'assessment' : 'assessments'} to complete this course.
                          </>
                        )}
                      </p>

                      {assessmentGroups.map((group) => (
                        <div key={group.key}>
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                            {group.label}
                          </p>
                          <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
                            {group.items.map((item) => (
                              <div
                                key={item.assessment_id}
                                className="flex items-center justify-between gap-3 px-4 py-3 bg-white"
                              >
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    <span
                                      className={`inline-flex items-center px-1.5 py-0.5 mr-2 rounded text-[10px] font-bold align-middle ${
                                        item.assessment_type === 'PRE'
                                          ? 'bg-blue-100 text-blue-700'
                                          : 'bg-purple-100 text-purple-700'
                                      }`}
                                    >
                                      {item.assessment_type}
                                    </span>
                                    {item.title}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {item.total_questions} {item.total_questions === 1 ? 'question' : 'questions'}
                                    {item.total_questions === 0 && ' · cannot be passed until questions are added'}
                                  </p>
                                </div>
                                <label className="flex items-center gap-2 flex-shrink-0 cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={item.required_for_completion}
                                    disabled={isSaving}
                                    onChange={() => toggleAssessmentRequired(item)}
                                    className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                  />
                                  <span
                                    className={`text-xs font-medium ${
                                      item.required_for_completion ? 'text-green-700' : 'text-gray-500'
                                    }`}
                                  >
                                    Must pass
                                  </span>
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  </div>
                )}
              </div>

              {/* HIDDEN - Assessment Tab - Keeping for future use */}
              {/* {activeTab === 'assessment' && (
            <div className="space-y-6">
              <div className="border rounded-lg p-6 bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Assessment Overview</h3>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Passing Score (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formValues.assessment_overview?.passing_score || 70}
                    onChange={(e) => setValue('assessment_overview', {
                      ...formValues.assessment_overview,
                      passing_score: parseInt(e.target.value) || 70
                    } as any)}
                    className="w-full max-w-xs px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700">Assessment Components</label>
                    <button
                      onClick={addAssessmentComponent}
                      className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Add Component
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    {formValues.assessment_overview?.components?.map((component: AssessmentComponent, index: number) => (
                      <div key={component._id || index} className="flex gap-2">
                        <input
                          type="text"
                          value={component.name}
                          onChange={(e) => updateAssessmentComponent(index, 'name', e.target.value)}
                          className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Component name (e.g., Quizzes)"
                        />
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={component.weight}
                          onChange={(e) => updateAssessmentComponent(index, 'weight', parseInt(e.target.value) || 0)}
                          className="w-24 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Weight %"
                        />
                        <button
                          onClick={() => removeAssessmentComponent(index)}
                          className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {(!formValues.assessment_overview?.components || formValues.assessment_overview.components.length === 0) && (
                      <div className="text-gray-500 text-sm py-4 text-center border-2 border-dashed rounded-lg">
                        No assessment components defined. Click "Add Component" to start.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )} */}

              {/* HIDDEN - Final Project Tab - Keeping for future use */}
              {/* {activeTab === 'project' && (
            <div className="space-y-6">
              <div className="border rounded-lg p-6 bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Final Project</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project Title
                    </label>
                    <input
                      type="text"
                      value={formValues.final_project?.title || ''}
                      onChange={(e) => setValue('final_project', {
                        ...formValues.final_project,
                        title: e.target.value
                      } as any)}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter project title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project Description
                    </label>
                    <textarea
                      value={formValues.final_project?.description || ''}
                      onChange={(e) => setValue('final_project', {
                        ...formValues.final_project,
                        description: e.target.value
                      } as any)}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows={3}
                      placeholder="Describe the final project requirements"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Estimated Time (Hours)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formValues.final_project?.estimated_time?.hours || 0}
                        onChange={(e) => setValue('final_project', {
                          ...formValues.final_project,
                          estimated_time: {
                            ...formValues.final_project?.estimated_time,
                            hours: parseInt(e.target.value) || 0
                          }
                        } as any)}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Estimated Time (Minutes)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={formValues.final_project?.estimated_time?.minutes || 0}
                        onChange={(e) => setValue('final_project', {
                          ...formValues.final_project,
                          estimated_time: {
                            ...formValues.final_project?.estimated_time,
                            minutes: parseInt(e.target.value) || 0
                          }
                        } as any)}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )} */}

              {/* HIDDEN - Next Steps Tab - Keeping for future use */}
              {/* {activeTab === 'next' && (
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="text-sm font-medium text-gray-700">
                    Next Topics to Learn
                  </label>
                  <button
                    onClick={() => {
                      const topics = [...(formValues.next_steps?.topics || [])];
                      topics.push('');
                      setValue('next_steps', {
                        ...formValues.next_steps,
                        topics
                      } as any);
                    }}
                    className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add Topic
                  </button>
                </div>
                <div className="space-y-2">
                  {formValues.next_steps?.topics?.map((topic: string, index: number) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={topic}
                        onChange={(e) => {
                          const topics = [...(formValues.next_steps?.topics || [])];
                          topics[index] = e.target.value;
                          setValue('next_steps', {
                            ...formValues.next_steps,
                            topics
                          } as any);
                        }}
                        className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter next topic to learn"
                      />
                      <button
                        onClick={() => {
                          const topics = [...(formValues.next_steps?.topics || [])];
                          topics.splice(index, 1);
                          setValue('next_steps', {
                            ...formValues.next_steps,
                            topics
                          } as any);
                        }}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )} */}
              </fieldset>
            </div>
          </div>
        </div>

        {/* Footer with Action Buttons. Saving is pointless on a published course,
            so the footer collapses to a Close button. */}
        {activeTab !== 'delete' && activeTab !== 'translations' && (
          <div className="bg-white border-t px-4 sm:px-6 py-3 sm:py-4 rounded-b-xl">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <p className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
                {isPublished
                  ? 'Published courses are read-only'
                  : 'Make sure to save your changes before closing'}
              </p>
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={onClose}
                  disabled={isSaving}
                  className="flex-1 sm:flex-none px-4 sm:px-5 py-2 sm:py-2.5 text-sm sm:text-base text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {isPublished ? 'Close' : 'Cancel'}
                </button>
                {!isPublished && (
                <button
                  type="button"
                  onClick={handleSubmit(onSubmit, onError)}
                  disabled={isSaving}
                  className="flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {isSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      <span>Save Changes</span>
                    </>
                  )}
                </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div >
  );
}

// Learning Objective Form Component
function LearningObjectiveForm({
  objective,
  onSave,
  onCancel,
}: {
  objective: LearningObjective | null;
  onSave: (objective: LearningObjective) => void;
  onCancel: () => void;
}) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LearningObjective>({
    defaultValues: {
      description: objective?.description || '',
      bloom_level: objective?.bloom_level || 'understand',
    },
  });

  const bloomLevelOptions: SelectOption[] = BLOOM_LEVELS.map((level) => ({
    label: `${level.label} - ${level.description}`,
    value: level.value,
  }));

  const onSubmitObjective = (data: LearningObjective) => {
    onSave(data);
  };

  return (
    <div className="border rounded-lg p-4 bg-blue-50 mb-4">
      <form onSubmit={handleSubmit(onSubmitObjective)} className="space-y-4">
        <Textarea
          name="description"
          control={control}
          label="Learning Objective Description"
          required
          error={errors.description?.message as string}
          rows={2}
          placeholder="Describe what students will be able to do after completing this course"
          rules={{ required: 'Description is required' }}
        />
        <Select
          name="bloom_level"
          control={control}
          label="Bloom's Taxonomy Level"
          options={bloomLevelOptions}
          placeholder="Select Bloom Level"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {objective ? 'Update' : 'Add'} Objective
          </button>
        </div>
      </form>
    </div>
  );
}

// Resource Form Component
function ResourceForm({
  resource,
  onSave,
  onCancel,
}: {
  resource: Resource | null;
  onSave: (resource: Resource) => void;
  onCancel: () => void;
}) {
  const {
    control,
    handleSubmit,
  } = useForm<Resource>({
    defaultValues: {
      title: resource?.title || '',
      type: resource?.type || 'documentation',
      url: resource?.url || '',
      description: resource?.description || '',
    },
  });

  const resourceTypeOptions: SelectOption[] = [
    { label: 'Documentation', value: 'documentation' },
    { label: 'Video', value: 'video' },
    { label: 'Article', value: 'article' },
    { label: 'Tutorial', value: 'tutorial' },
    { label: 'Github', value: 'github' },
    { label: 'Website', value: 'website' },
    { label: 'Book', value: 'book' },
    { label: 'Tool', value: 'tool' },
    { label: 'Other', value: 'other' },
  ];

  const onSubmitResource = (data: Resource) => {
    onSave(data);
  };

  return (
    <div className="border rounded-lg p-4 bg-blue-50">
      <form onSubmit={handleSubmit(onSubmitResource)} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            name="title"
            control={control}
            label="Resource Title"
            required
            placeholder="e.g., Official Documentation"
            rules={{ required: 'Title is required' }}
          />
          <Select
            name="type"
            control={control}
            label="Resource Type"
            options={resourceTypeOptions}
            placeholder="Select Type"
          />
        </div>
        {/* type="url" - the shared Input skips the free-text check for URLs. */}
        <Input
          name="url"
          control={control}
          type="url"
          label="Resource URL"
          required
          placeholder="https://example.com/resource"
          rules={{ required: 'URL is required' }}
        />
        <Textarea
          name="description"
          control={control}
          label="Description (Optional)"
          rows={2}
          placeholder="Brief description of the resource"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {resource ? 'Update' : 'Add'} Resource
          </button>
        </div>
      </form>
    </div>
  );
}
